import { useEffect, useState } from 'react';

/**
 * THE HEADER'S BRANDING, FETCHED ONCE (#251).
 *
 * ---- ⛔ IT NEVER BLOCKS AND NEVER FAILS ------------------------------------
 *
 * ⚠️ THIS RUNS BEFORE THE FIRST PAINT AND BEFORE SIGN-IN. A hook that could
 * return "loading" and let a caller render nothing would produce a header that
 * flashes empty on every page load; one that could throw would produce a white
 * screen when the API is slow. So it STARTS at the shipped defaults and swaps in
 * the company's branding if and when it arrives.
 *
 * ⭐ WHICH ALSO MEANS AN UNBRANDED COMPANY COSTS NOTHING VISIBLE. The defaults are
 * exactly today's header, so the "before" and the "after" are identical until
 * somebody sets something.
 *
 * ---- ⚠️ MODULE-LEVEL CACHE, LIKE useMe -------------------------------------
 *
 * The header, the sign-in screen and the branding config screen all want this.
 * Fetching per mount would send the logo three times; the cache is the same
 * pattern `useMe` uses, for the same reason.
 *
 * ⛔ AND IT IS INVALIDATED FROM THE CONFIG SCREEN. A cache nobody can clear is
 * why an admin saves a colour, sees no change, and saves it again.
 */

export interface HeaderBranding {
    companyName: string;
    appName: string;
    schedulerTitle: string;
    logo: string | null;
    /**
     * ⭐ THE SERVER HAS ALWAYS SENT THESE TWO; this side never declared them.
     * ⚠️ tools/branding-default-guard.cjs WAS RED because of it -- server=14
     * keys, web=12 -- and had been since they were added. ⛔ It is not in
     * deploy-frontend-dockboard.ps1's preflight, so nothing surfaced it.
     * §1M.6: "LogoDarkPath is separate because dockview is dark."
     */
    logoDark: string | null;
    icon: string | null;
    headingFontKey: string;
    companyNameSizePx: number;
    schedulerTitleSizePx: number;
    schedulerTitleWeight: number;
    headerBgHex: string | null;
    headerTextHex: string | null;
    primaryHex: string | null;
    accentHex: string | null;
}

/**
 * ⚠️ A SECOND COPY OF THE SERVER'S DEFAULTS, AND THE ONLY ONE IN THIS REPO.
 *
 * ⛔ IT EXISTS BECAUSE THERE IS A MOMENT BEFORE THE FETCH RETURNS, and nothing
 * can be rendered without values. web/ cannot import from src/, so this cannot be
 * shared — which makes it exactly the kind of duplicate that drifts (§1R.16).
 *
 * ⭐ SO IT IS PINNED BY A GUARD: tools/branding-default-guard.cjs compares this
 * object against DEFAULT_BRANDING in src/lib/branding.ts and fails the build if
 * they diverge. A comment asking the next person to remember would not have.
 */
export const FALLBACK_BRANDING: HeaderBranding = {
    companyName: 'Bardotek',   // F810 - pinned to DEFAULT_BRANDING by tools/branding-default-guard.cjs
    appName: 'Dock Scheduler',
    schedulerTitle: 'Dock Schedule',
    logo: null,
    logoDark: null,
    icon: null,
    headingFontKey: 'serif',
    companyNameSizePx: 18,
    schedulerTitleSizePx: 28,
    schedulerTitleWeight: 600,
    headerBgHex: null,
    headerTextHex: null,
    primaryHex: null,
    accentHex: null,
};

/**
 * ⭐⭐ THE CACHE IS KEYED BY APP CODE (2026-09-07).
 *
 * ⚠️ IT USED TO BE A SINGLE SLOT, which was correct while `/api/brand` answered
 * one company-level blob for everything. ⛔ It is not correct now that the
 * carrier portal and the scheduler resolve to DIFFERENT headers: whichever shell
 * mounted first would have populated the slot and the other would have rendered
 * its branding.
 *
 * ⚠️ AND THE CSS VARIABLES ARE GLOBAL — they live on document.documentElement.
 * That is fine because these are separate ROUTES, not two shells on one screen;
 * the last one mounted owns the document, which is the one the user is looking
 * at. ⛔ If two branded shells ever render simultaneously, this is the line that
 * has to change.
 */
type Entry = { branding: HeaderBranding; cssVars: Record<string, string> };
const cache = new Map<string, Entry>();
const inFlight = new Map<string, Promise<void>>();
const listeners = new Set<() => void>();

/** ⚠️ DOCK IS THE DEFAULT, matching the server. Every caller that predates the
 *  app parameter keeps working and keeps getting the scheduler's header. */
const DEFAULT_APP = 'DOCK';

/**
 * ⛔ THE VARIABLES ARE SET ON document.documentElement, NOT ON A COMPONENT.
 *
 * ⚠️ The header is not the only thing that reads them — the sign-in screen and the
 * branding preview do too — and a React-owned style object would have to be
 * threaded through every one of them. Setting custom properties at the root is
 * what CSS variables are for.
 *
 * ⛔ AND ONLY KEYS THAT START WITH '--brand-' ARE WRITTEN. The values were proved
 * safe on the server, but this is the line where a response reaches the document's
 * own style, so it does not take the server's word for the KEY either.
 */
function applyVars(vars: Record<string, string>): void {
    const root = document.documentElement;
    for (const [k, v] of Object.entries(vars ?? {})) {
        if (!k.startsWith('--brand-')) continue;
        if (typeof v !== 'string' || /[;{}]/.test(v)) continue;
        root.style.setProperty(k, v);
    }
}

/**
 * ⭐⭐ THE PARTS THAT ARE NOT IN THE RENDERED TREE: the tab's title and icon.
 *
 * ⚠️ web/index.html hardcodes `<title>Symbia Dock Scheduler</title>` and a
 * `<link rel="icon" href="/favicon.svg">`. ⛔ So Dock's HEADER was branded and
 * its TAB was not -- an inconsistency created on 2026-09-07, the same day
 * Pipeline and the labour clock both started setting theirs from branding.
 *
 * ⛔ IT NEVER THROWS. A tab title is the least important thing on the screen and
 * must never be able to take the app down with it.
 */
function applyDocumentChrome(b: HeaderBranding): void {
    try {
        // ⚠️ THE PAGE FACT FIRST, then the company -- "Appointments · Acme",
        //    which is what a row of pinned tabs needs to be told apart.
        const parts = [b.schedulerTitle, b.companyName].filter(Boolean);
        if (parts.length) document.title = parts.join(' \u00b7 ');

        // ⛔ ONLY REPLACED WHEN A CUSTOMER HAS ONE. Unset, /favicon.svg stays --
        //    which is Symbia's shipped mark and the correct answer for an
        //    unbranded company.
        if (b.icon) {
            let link = document.querySelector('link[rel="icon"]') as HTMLLinkElement | null;
            if (!link) {
                link = document.createElement('link');
                link.setAttribute('rel', 'icon');
                document.head.appendChild(link);
            }
            // ⚠️ type= is REMOVED, not overwritten. The shipped icon declares
            //    image/svg+xml and a customer's is a PNG; leaving the old type
            //    on a new href is how a browser quietly refuses to draw it.
            link.removeAttribute('type');
            link.setAttribute('href', b.icon);
        }
    } catch {
        /* ⛔ A tab is not worth a white screen. */
    }
}

async function load(appCode: string): Promise<void> {
    try {
        // ⭐ THE APP CODE TRAVELS. The server resolves per app from
        //    dbo.vBrandingResolved (migration 084); this only says which one.
        const res = await fetch(`/api/brand?app=${encodeURIComponent(appCode)}`, { credentials: 'include' });
        if (!res.ok) return;                       // ⚠️ Keep the defaults. Not an error.
        const data = await res.json() as { branding: HeaderBranding; cssVars: Record<string, string> };
        if (!data || !data.branding) return;
        const entry: Entry = { branding: data.branding, cssVars: data.cssVars ?? {} };
        cache.set(appCode, entry);
        applyVars(entry.cssVars);
        applyDocumentChrome(entry.branding);
        listeners.forEach(fn => fn());
    } catch {
        // ⛔ SILENT ON PURPOSE. An unbranded header is not something to tell a
        // scheduler about; it is indistinguishable from a company that set nothing.
    }
}

/** ⭐ Called by the config screen after a save, so the header updates in place. */
export function refreshBranding(
    next?: { branding: HeaderBranding; cssVars?: Record<string, string> },
    appCode: string = DEFAULT_APP,
): void {
    if (next?.branding) {
        const entry: Entry = { branding: next.branding, cssVars: next.cssVars ?? cache.get(appCode)?.cssVars ?? {} };
        cache.set(appCode, entry);
        applyVars(entry.cssVars);
        applyDocumentChrome(entry.branding);
        listeners.forEach(fn => fn());
        return;
    }
    /**
     * ⛔ CLEARS EVERY APP, NOT JUST THIS ONE. The config screen saves the COMPANY
     * tier, which every app inherits from — so invalidating only the app whose
     * screen you were standing on would leave the other five showing the value
     * that was just replaced. ⚠️ A cache nobody can fully clear is why an admin
     * saves a colour, sees no change, and saves it again.
     */
    cache.clear();
    inFlight.clear();
    void useBrandingRefetch(appCode);
}

function useBrandingRefetch(appCode: string): Promise<void> {
    const existing = inFlight.get(appCode);
    if (existing) return existing;
    const p = load(appCode).finally(() => { inFlight.delete(appCode); });
    inFlight.set(appCode, p);
    return p;
}

/**
 * ⭐ `useBranding('CARRIER')` — the shell says which app it is.
 * ⚠️ The parameter defaults to DOCK so no existing caller changes behaviour.
 */
export function useBranding(appCode: string = DEFAULT_APP): HeaderBranding {
    const [branding, setBranding] = useState<HeaderBranding>(
        cache.get(appCode)?.branding ?? FALLBACK_BRANDING);

    useEffect(() => {
        const onChange = () => setBranding(cache.get(appCode)?.branding ?? FALLBACK_BRANDING);
        listeners.add(onChange);
        const hit = cache.get(appCode);
        if (hit) {
            // ⚠️ Re-apply on mount: a previous page may have cleared the root
            //    style, or another app's shell may have overwritten it.
            applyVars(hit.cssVars);
            applyDocumentChrome(hit.branding);
            onChange();
        } else {
            void useBrandingRefetch(appCode);
        }
        return () => { listeners.delete(onChange); };
    // ⛔ appCode IS A DEPENDENCY. Without it, navigating from the scheduler to
    //    the carrier portal would keep the scheduler's branding on screen.
    }, [appCode]);

    return branding;
}
